import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Loader2, SendHorizonal, Save, Trash2, Image, MapPin, Compass, LandPlot } from 'lucide-react';
import { QuotaUpsell } from './QuotaUpsell';

const ethiopianCities = [
  'Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle',
  'Dire Dawa', 'Jimma', 'Gondar', 'Dessie', 'Bishoftu',
  'Shashamane', 'Arba Minch', 'Harar', 'Debre Markos', 'Nekemte',
];

const plotPurposes = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'mixed', label: 'Mixed Use' },
  { value: 'agricultural', label: 'Agricultural' },
];

const facingDirections = [
  { value: 'north', label: 'North' },
  { value: 'south', label: 'South' },
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' },
  { value: 'north_east', label: 'North East' },
  { value: 'north_west', label: 'North West' },
  { value: 'south_east', label: 'South East' },
  { value: 'south_west', label: 'South West' },
];

export function AddPlot() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Check if agent has agency membership
  const { data: agentProfile, isLoading: checkingAgency } = useQuery({
    queryKey: ['agent-agency-check-plot', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agent_profiles').select('agency_id').eq('user_id', user!.id).single();
      return data;
    },
    enabled: !!user && hasRole('agent'),
  });

  const isAgentWithoutAgency = hasRole('agent') && !hasRole('agency_admin') && !hasRole('superadmin') && !checkingAgency && !agentProfile?.agency_id;

  // Form state
  const [listingType, setListingType] = useState<'sale' | 'rent'>('sale');
  const [city, setCity] = useState('');
  const [subCity, setSubCity] = useState('');
  const [woreda, setWoreda] = useState('');
  const [address, setAddress] = useState('');

  const [titleEn, setTitleEn] = useState('');
  const [titleAm, setTitleAm] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descAm, setDescAm] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('ETB');
  const [areaSqm, setAreaSqm] = useState('');

  // Plot-specific
  const [roadWidthFt, setRoadWidthFt] = useState('');
  const [facingDirection, setFacingDirection] = useState('');
  const [plotPurpose, setPlotPurpose] = useState('residential');
  const [isCorner, setIsCorner] = useState(false);
  const [isParkFace, setIsParkFace] = useState(false);
  const [isMainRoad, setIsMainRoad] = useState(false);

  const [contactPerson, setContactPerson] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [images, setImages] = useState<{ url: string; is_primary: boolean }[]>([]);
  const [uploading, setUploading] = useState(false);

  // Pre-fill contact from profile
  useQuery({
    queryKey: ['my-profile-for-add-plot'],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase.from('profiles').select('*').eq('user_id', user.id).single();
      if (data) {
        if (!contactPerson && data.full_name) setContactPerson(data.full_name);
        if (!contactPhone && data.phone) setContactPhone(data.phone);
        if (!contactEmail && user.email) setContactEmail(user.email);
      }
      return data;
    },
    enabled: !!user,
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !user) return;
    setUploading(true);
    try {
      const newImages: { url: string; is_primary: boolean }[] = [];
      for (const file of Array.from(e.target.files)) {
        const ext = file.name.split('.').pop();
        const path = `${user.id}/plots/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage.from('property-images').upload(path, file);
        if (error) throw error;
        const { data: urlData } = supabase.storage.from('property-images').getPublicUrl(path);
        newImages.push({ url: urlData.publicUrl, is_primary: images.length === 0 && newImages.length === 0 });
      }
      setImages(prev => [...prev, ...newImages]);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx: number) => {
    setImages(prev => {
      const updated = prev.filter((_, i) => i !== idx);
      if (updated.length > 0 && !updated.some(img => img.is_primary)) updated[0].is_primary = true;
      return updated;
    });
  };

  const setPrimaryImage = (idx: number) => {
    setImages(prev => prev.map((img, i) => ({ ...img, is_primary: i === idx })));
  };

  const saveMutation = useMutation({
    mutationFn: async (submitForReview: boolean) => {
      if (!user) throw new Error('Not authenticated');
      if (!titleEn || !city || !price) throw new Error('Please fill all required fields (Title, City, Price)');

      const slug = titleEn.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') + '-' + Date.now().toString(36);

      const { data: plot, error } = await supabase
        .from('plots')
        .insert({
          user_id: user.id,
          title: { en: titleEn, am: titleAm || titleEn, om: '' },
          description: { en: descEn, am: descAm || descEn, om: '' },
          listing_type: listingType,
          price: parseFloat(price),
          currency: currency as any,
          city,
          sub_city: subCity || null,
          woreda: woreda || null,
          address: address || null,
          area_sqm: areaSqm ? parseFloat(areaSqm) : null,
          road_width_ft: roadWidthFt ? parseFloat(roadWidthFt) : null,
          facing_direction: facingDirection || null,
          plot_purpose: plotPurpose as any,
          is_corner: isCorner,
          is_park_face: isParkFace,
          is_main_road: isMainRoad,
          contact_person: contactPerson || null,
          contact_phone: contactPhone || null,
          contact_email: contactEmail || null,
          slug,
          status: submitForReview ? 'active' : 'draft',
          approval_status: submitForReview ? 'pending_review' : 'draft',
        } as any)
        .select()
        .single();

      if (error) throw error;

      if (images.length > 0) {
        const imgRows = images.map((img, i) => ({
          plot_id: plot.id,
          url: img.url,
          is_primary: img.is_primary,
          sort_order: i,
        }));
        const { error: imgErr } = await supabase.from('plot_images').insert(imgRows as any);
        if (imgErr) throw imgErr;
      }

      return plot;
    },
    onSuccess: (_, submitForReview) => {
      queryClient.invalidateQueries({ queryKey: ['my-plots'] });
      toast.success(submitForReview ? 'Plot submitted for review!' : 'Plot saved as draft!');
      navigate('/dashboard/plots');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const areaSqft = areaSqm ? (parseFloat(areaSqm) * 10.764).toFixed(0) : '';

  if (isAgentWithoutAgency) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <h2 className="font-display text-xl font-bold mb-2">Agency Membership Required</h2>
        <p className="text-muted-foreground mb-4">You must belong to an agency to post plots. Contact your agency admin for an invite link.</p>
        <Button onClick={() => navigate('/agencies')}>Browse Agencies</Button>
      </div>
    );
  }

  return (
    <QuotaUpsell kind="listing">
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/plots')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <LandPlot className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-bold">Add New Plot / Land</h2>
        </div>
      </div>

      {/* Section 1: Purpose & Location */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" />Purpose & Location</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">Purpose *</Label>
            <div className="flex gap-2">
              <Button type="button" variant={listingType === 'sale' ? 'default' : 'outline'} onClick={() => setListingType('sale')} className="flex-1">For Sale</Button>
              <Button type="button" variant={listingType === 'rent' ? 'default' : 'outline'} onClick={() => setListingType('rent')} className="flex-1">For Rent / Lease</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>City *</Label>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent>{ethiopianCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Sub City</Label><Input value={subCity} onChange={e => setSubCity(e.target.value)} placeholder="e.g. Bole" /></div>
            <div><Label>Woreda</Label><Input value={woreda} onChange={e => setWoreda(e.target.value)} placeholder="e.g. Woreda 03" /></div>
          </div>
          <div><Label>Address / Landmark</Label><Input value={address} onChange={e => setAddress(e.target.value)} placeholder="Near Edna Mall, Bole Road" /></div>
        </CardContent>
      </Card>

      {/* Section 2: Plot Details */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><LandPlot className="h-4 w-4" />Plot Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Title (English) *</Label><Input value={titleEn} onChange={e => setTitleEn(e.target.value)} placeholder="e.g. 200sqm Residential Plot in Bole" /></div>
            <div><Label>Title (Amharic)</Label><Input value={titleAm} onChange={e => setTitleAm(e.target.value)} placeholder="አማርኛ ርዕስ" /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Description (English)</Label><Textarea value={descEn} onChange={e => setDescEn(e.target.value)} rows={4} placeholder="Describe the plot..." /></div>
            <div><Label>Description (Amharic)</Label><Textarea value={descAm} onChange={e => setDescAm(e.target.value)} rows={4} placeholder="ስለ ቦታው ይግለጹ..." /></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Price *</Label>
              <Input type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder="0" />
            </div>
            <div>
              <Label>Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ETB">ETB (Birr)</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Area (m²)</Label>
              <Input type="number" value={areaSqm} onChange={e => setAreaSqm(e.target.value)} placeholder="0" />
              {areaSqft && <p className="text-xs text-muted-foreground mt-1">≈ {areaSqft} sqft</p>}
            </div>
            <div>
              <Label>Plot Purpose</Label>
              <Select value={plotPurpose} onValueChange={setPlotPurpose}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{plotPurposes.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Plot-Specific Features */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Compass className="h-4 w-4" />Plot Features</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Road Width (ft)</Label>
              <Input type="number" value={roadWidthFt} onChange={e => setRoadWidthFt(e.target.value)} placeholder="e.g. 40" />
            </div>
            <div>
              <Label>Facing Direction</Label>
              <Select value={facingDirection} onValueChange={setFacingDirection}>
                <SelectTrigger><SelectValue placeholder="Select direction" /></SelectTrigger>
                <SelectContent>{facingDirections.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center justify-between rounded-xl border p-4">
              <div>
                <Label className="text-sm font-medium">Corner Plot</Label>
                <p className="text-xs text-muted-foreground">Located at a corner intersection</p>
              </div>
              <Switch checked={isCorner} onCheckedChange={setIsCorner} />
            </div>
            <div className="flex items-center justify-between rounded-xl border p-4">
              <div>
                <Label className="text-sm font-medium">Park Face</Label>
                <p className="text-xs text-muted-foreground">Faces a park or open area</p>
              </div>
              <Switch checked={isParkFace} onCheckedChange={setIsParkFace} />
            </div>
            <div className="flex items-center justify-between rounded-xl border p-4">
              <div>
                <Label className="text-sm font-medium">Main Road</Label>
                <p className="text-xs text-muted-foreground">Located on a main road</p>
              </div>
              <Switch checked={isMainRoad} onCheckedChange={setIsMainRoad} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Images */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Image className="h-4 w-4" />Photos</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed rounded-xl p-8 justify-center hover:border-primary transition-colors">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5 text-muted-foreground" />}
            <div className="text-center">
              <span className="text-sm font-medium">{uploading ? 'Uploading...' : 'Click to upload plot photos'}</span>
              <p className="text-xs text-muted-foreground mt-1">Upload high quality images (max 10MB each)</p>
            </div>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleImageUpload} disabled={uploading} />
          </label>
          {images.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {images.map((img, i) => (
                <div key={i} className="relative group cursor-pointer" onClick={() => setPrimaryImage(i)}>
                  <img src={img.url} alt="" className="h-24 w-full object-cover rounded-lg" />
                  {img.is_primary && <span className="absolute top-1 left-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded">Cover</span>}
                  <button onClick={(e) => { e.stopPropagation(); removeImage(i); }} className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 5: Contact Details */}
      <Card>
        <CardHeader><CardTitle className="text-base">Contact Information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Contact Person</Label><Input value={contactPerson} onChange={e => setContactPerson(e.target.value)} placeholder="Your name" /></div>
          <div><Label>Phone</Label><Input value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+251..." /></div>
          <div><Label>Email</Label><Input value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="email@example.com" /></div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-end pb-8">
        <Button variant="outline" onClick={() => saveMutation.mutate(false)} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-1" />Save as Draft
        </Button>
        <Button onClick={() => saveMutation.mutate(true)} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <SendHorizonal className="h-4 w-4 mr-1" />}
          Submit for Review
        </Button>
      </div>
    </div>
    </QuotaUpsell>
  );
}
